import React, { type FC } from 'react';
import { useRouter } from 'expo-router';
import { FileText, Target, ArrowRight } from 'lucide-react';
import { useAui, useAuiState } from '@assistant-ui/react';
import { useTranslation } from 'react-i18next';
import { CardShell } from './card-shell';
import type { ToolCardProps } from './types';
import { safeGet } from './types';
import { buildTailoredCvRoute } from '../../../lib/cv-target-route';
import { runIfComposerAvailable } from '../composer-errors';
import { resolveCvOnePageFeedback } from './cv-one-page-feedback';

const QUICK_ACTIONS = ['Publish this CV', 'Tailor for a job', 'Export', 'Refine'];

const cardActionStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 10px',
  borderRadius: 8,
  border: '1px solid var(--gem-outline-variant)',
  background: 'rgba(0,0,0,0.04)',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export const CVDetailCard: FC<ToolCardProps> = ({ result, toolName, args }) => {
  const router = useRouter();
  const aui = useAui();
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const { t } = useTranslation('seeker');

  if (!result || typeof result !== 'object') return null;

  const isTailor = toolName === 'cvTailor';
  const isOnePage = toolName === 'cvSetFitToOnePage';
  const onePageFeedback = isOnePage ? resolveCvOnePageFeedback(result, args) : null;
  const targetTitle = safeGet<string>(result, 'targetJob', 'title') || safeGet<string>(args, 'targetTitle');
  const targetCompany =
    safeGet<string>(result, 'targetJob', 'companyName') || safeGet<string>(args, 'targetCompany');
  const targetLabel =
    safeGet<string>(result, 'targetLabel') ||
    (targetTitle ? [targetTitle, targetCompany ? `at ${targetCompany}` : null].filter(Boolean).join(' ') : '');
  const cvTitle =
    safeGet<string>(result, 'displayTitle') ||
    safeGet<string>(result, 'title') ||
    (isTailor && targetTitle ? `${targetTitle} CV` : isTailor ? 'Tailored CV' : 'CV');
  const title = onePageFeedback ? t(onePageFeedback.titleKey) : cvTitle;
  const summary = onePageFeedback ? t(onePageFeedback.bodyKey) : safeGet<string>(result, 'summary');
  const templateId = safeGet<string>(result, 'templateId');
  const isPublic = safeGet<boolean>(result, 'isPublic');
  const cvId =
    safeGet<string>(result, 'id')?.trim() ||
    safeGet<string>(result, 'cvId')?.trim() ||
    undefined;
  const isCreate = toolName === 'cvCreate';
  const jobId =
    safeGet<string>(args, 'catalogJobId') ||
    safeGet<string>(args, 'jobId') ||
    safeGet<string>(result, 'targetJob', 'id');

  const sendQuick = (text: string) => {
    if (isRunning) return;
    const send = () => {
      const composer = aui.composer();
      composer.setText(text);
      composer.send();
    };

    if (!runIfComposerAvailable(send) && typeof window !== 'undefined') {
      window.setTimeout(() => {
        runIfComposerAvailable(send);
      }, 0);
    }
  };

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Header: icon + label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500 }}>
            {isOnePage ? t('chat.cvOnePage.cardLabel') : 'CV'}
          </span>
        </div>

        {/* Title */}
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--text-primary)',
            lineHeight: '22px',
          }}
        >
          {title}
        </div>

        {isOnePage && cvTitle !== 'CV' && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
              lineHeight: '18px',
            }}
          >
            {cvTitle}
          </div>
        )}

        {/* Summary */}
        {summary && (
          <div
            style={{
              fontSize: 14,
              color: 'var(--text-secondary)',
              lineHeight: '20px',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {summary}
          </div>
        )}

        {/* Badges row */}
        {!isOnePage && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {templateId && (
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--text-secondary)',
                  background: 'rgba(0,0,0,0.04)',
                  borderRadius: 9999,
                  padding: '2px 8px',
                  lineHeight: '16px',
                }}
              >
                {templateId}
              </span>
            )}
            <span
              style={{
                fontSize: 11,
                color: isPublic ? 'var(--text-secondary)' : 'var(--text-tertiary)',
                background: isPublic ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
                borderRadius: 9999,
                padding: '2px 8px',
                lineHeight: '16px',
              }}
            >
              {isPublic ? 'Public' : 'Private'}
            </span>
          </div>
        )}

        {/* Tailored for job reference */}
        {isTailor && (targetLabel || jobId) && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Target size={13} style={{ flexShrink: 0 }} />
            <span>{targetLabel ? `Tailored for ${targetLabel}` : `Tailored for job ${jobId}`}</span>
          </div>
        )}

        {/* Open in tailored CV view */}
        {isTailor && cvId && (
          <button
            type="button"
            onClick={() => router.push(buildTailoredCvRoute(cvId))}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid var(--gem-outline-variant)',
              background: 'rgba(0,0,0,0.04)',
              color: 'var(--text-primary)',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
              alignSelf: 'flex-start',
            }}
          >
            View tailored CV
            <ArrowRight size={13} />
          </button>
        )}

        {isOnePage && cvId && (
          <button
            type="button"
            onClick={() => router.push(buildTailoredCvRoute(cvId))}
            style={{ ...cardActionStyle, alignSelf: 'flex-start' }}
          >
            {t('chat.cvOnePage.viewCv')}
            <ArrowRight size={13} />
          </button>
        )}

        {/* Open editor + quick-action chips for newly created CV */}
        {isCreate && cvId && (
          <>
            <button
              type="button"
              onClick={() => router.push(buildTailoredCvRoute(cvId))}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                borderRadius: 8,
                border: '1px solid var(--gem-outline-variant)',
                background: 'rgba(0,0,0,0.04)',
                color: 'var(--text-primary)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                alignSelf: 'flex-start',
              }}
            >
              Open editor
              <ArrowRight size={13} />
            </button>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {QUICK_ACTIONS.map((label) => (
                <button
                  key={label}
                  type="button"
                  disabled={isRunning}
                  onClick={() => sendQuick(label)}
                  style={{
                    border: '1px solid var(--gem-outline-variant)',
                    background: 'none',
                    borderRadius: 999,
                    padding: '4px 10px',
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--text-secondary)',
                    cursor: isRunning ? 'default' : 'pointer',
                    fontFamily: 'inherit',
                    opacity: isRunning ? 0.4 : 1,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </CardShell>
  );
};
